import { useCallback } from 'react';
import type { ErrorOption } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';

export const CONNECTION_API_ERRORS = {
  connectionAlreadyConnected: 'ConnectionAlreadyConnectedError',
  amazonConnectionRegionAbsent: 'AmazonConnectionRegionAbsentError',
} as const;

export interface ConnectionApiError {
  type?: string;
  message?: string;
}

/** Reads the `{ error: { type, message } }` body the connection APIs reject with. */
export const getConnectionApiError = (
  error: unknown,
): ConnectionApiError | undefined =>
  (error as { response?: { data?: { error?: ConnectionApiError } } } | null)
    ?.response?.data?.error;

/**
 * The slice of a react-hook-form instance the handler writes to. Method
 * syntax with a plain `string` name so any `useForm<…>()` result is
 * assignable; errors target the `name` field, and `region` for Amazon.
 */
export interface ConnectionErrorForm {
  setError(name: string, error: ErrorOption): void;
  reset(): void;
}

export interface UseConnectionApiErrorHandlerOptions {
  /** Field errors are only set when a form is given; otherwise every error is a snackbar. */
  form?: ConnectionErrorForm;
  /**
   * Picks the snackbar title for errors that don't map to a field:
   * "Invalid Credentials" for username/password flows, "Something went
   * wrong" otherwise.
   */
  flow?: 'oauth' | 'credentials';
  /**
   * Rename forms: reset `form` back to the saved values when the error
   * doesn't map to a field, like legacy did.
   */
  resetOnError?: boolean;
}

/**
 * Ported from `packages/connections`' `useConnectionAPIErrorHandlers`: maps
 * a duplicate connection name and a missing Amazon region to form field
 * errors, and shows everything else as an error snackbar with the API's
 * message. Pass the returned handler as a mutation's `onError`.
 */
export function useConnectionApiErrorHandler({
  form,
  flow = 'oauth',
  resetOnError = false,
}: UseConnectionApiErrorHandlerOptions = {}): (error: unknown) => void {
  const { t } = useTranslation('@mint-lib/connections-ui');

  return useCallback(
    (error: unknown) => {
      const apiError = getConnectionApiError(error);

      if (
        form &&
        apiError?.type === CONNECTION_API_ERRORS.connectionAlreadyConnected
      ) {
        form.setError('name', {
          type: 'custom',
          message: t(
            'You have already used this name for another connection. Please enter a new name.',
          ),
        });
        return;
      }

      if (
        form &&
        apiError?.type === CONNECTION_API_ERRORS.amazonConnectionRegionAbsent
      ) {
        form.setError('region', {
          type: 'custom',
          message: t('This field is required.'),
        });
        return;
      }

      if (resetOnError) {
        form?.reset();
      }
      enqueueSnackbar({
        variant: 'error',
        title:
          flow === 'credentials'
            ? t('Invalid Credentials')
            : t('Something went wrong'),
        subTitle:
          apiError?.message ?? t('Please try again or contact the support.'),
        autoHideDuration: 6000,
      });
    },
    [flow, form, resetOnError, t],
  );
}
